import { Injectable, computed, signal } from '@angular/core';

/**
 * Safe fallback when no practice timezone is configured. Never browser-local —
 * appointment and audit instants must resolve to a real, stable zone.
 */
export const DEFAULT_PRACTICE_TIMEZONE = 'Africa/Johannesburg';

/**
 * PracticeTimezoneService
 *
 * App-agnostic holder for the two zones the timezone-aware date pipe needs:
 *
 *  - the **practice** zone (from `PracticeSettings.timezone`), and
 *  - the **device** zone (the viewer's own, resolved once from the browser).
 *
 * It lives in xmedical-core so the pipe can inject it without depending on any
 * app-level store. The app's `PracticeStore` (which lives in xmedical-app and
 * cannot be imported here) *feeds* it via `setPracticeTimezone` whenever
 * practice settings load, change, or are cleared on practice switch. Apps that
 * never feed it simply render in the default zone.
 *
 * Why a service and not a pipe argument: it lets every render site say
 * `value | practiceDateTime` with no per-template plumbing, and keeps the zone
 * in one reactive place that practice-switch updates.
 */
@Injectable({ providedIn: 'root' })
export class PracticeTimezoneService {
  /**
   * The configured practice zone, or null until settings load. Private so the
   * only way to change it is the explicit setter — easy to trace.
   */
  private readonly configuredZone = signal<string | null>(null);

  /**
   * The viewer's own IANA zone, resolved once at construction. Stable for the
   * session. Falls back to the default if the browser can't tell us.
   */
  readonly deviceTimezone: string = resolveDeviceTimezone();

  /**
   * Effective practice zone: the configured zone, or the safe default. This is
   * what the pipe formats against.
   */
  readonly practiceTimezone = computed<string>(
    () => this.configuredZone() ?? DEFAULT_PRACTICE_TIMEZONE
  );

  /**
   * Set (or clear, with null) the configured practice zone. Called by the
   * app's PracticeStore when practice settings load / change / clear.
   */
  setPracticeTimezone(zone: string | null): void {
    const trimmed = zone?.trim();
    // Validate before storing: an invalid IANA string from external config would
    // make dayjs.tz / Intl throw on every render. Treat invalid as unset so the
    // effective zone falls back to the safe default rather than breaking the app.
    this.configuredZone.set(trimmed && isValidTimeZone(trimmed) ? trimmed : null);
  }
}

/** True if `zone` is a usable IANA timezone (Intl accepts it without throwing). */
function isValidTimeZone(zone: string): boolean {
  try {
    Intl.DateTimeFormat('en', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Resolve the browser's IANA timezone, defaulting safely if unavailable.
 * Kept as a plain function so it's trivial to reason about and test.
 */
function resolveDeviceTimezone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && zone.length > 0 ? zone : DEFAULT_PRACTICE_TIMEZONE;
  } catch {
    return DEFAULT_PRACTICE_TIMEZONE;
  }
}
